import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/hooks/useCart";  // Iter v34b0
import { LangProvider } from "@/lib/i18n";

import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import PriceList from "@/pages/PriceList";
import PriceHistory from "@/pages/PriceHistory";
import PurchaseOrders from "@/pages/PurchaseOrders";
import SuratJalan from "@/pages/SuratJalan";
import Stock from "@/pages/Stock";
import StockLog from "@/pages/StockLog";
import Expenses from "@/pages/Expenses";
import Billing from "@/pages/Billing";
import BillingList from "@/pages/BillingList";
import ProfitLoss from "@/pages/ProfitLoss";
import CreditLimits from "@/pages/CreditLimits";  // Iter v34cq (#7) — Plafon kredit klien
import Users from "@/pages/Users";
import SystemLogs from "@/pages/SystemLogs";
import Settings from "@/pages/Settings";
import Chat from "@/pages/Chat";
import Pengumuman from "@/pages/Pengumuman";  // Iter v34b0 — replaces Chat page (broadcast email)
import Quotations from "@/pages/Quotations";
import VendorPO from "@/pages/VendorPO";  // Iter v34z (#2) — Vendor PO (procurement non-batching)
import MasterVendor from "@/pages/MasterVendor";  // Iter v34z (#5) — Master Vendor
// Iter v34z (#1) — Batching Plant module pages
import BatchingDashboard from "@/pages/BatchingDashboard";
import BatchingPo from "@/pages/BatchingPo";
import BatchingSj from "@/pages/BatchingSj";
import BatchingMaterialSj from "@/pages/BatchingMaterialSj";
import BatchingMaterialRekap from "@/pages/BatchingMaterialRekap";
import EmployeeRequests from "@/pages/EmployeeRequests";
import BatchingStock from "@/pages/BatchingStock";
import BatchingSolar from "@/pages/BatchingSolar";
import BatchingInputMaterial from "@/pages/BatchingInputMaterial";
import BatchingRekap from "@/pages/BatchingRekap";
import MasterBatching from "@/pages/MasterBatching";  // Iter v34z (#3 Phase C)
import ClientDashboard from "@/pages/ClientDashboard";
import ClientCatalog from "@/pages/ClientCatalog";
import ClientCart from "@/pages/ClientCart";  // Iter v34b0
import ClientNotifications from "@/pages/ClientNotifications";
import ClientSuratJalan from "@/pages/ClientSuratJalan";
import ClientInvoices from "@/pages/ClientInvoices";  // Iter v34{af}
import Signup from "@/pages/Signup";
import SignupVerify from "@/pages/SignupVerify";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PendingSignups from "@/pages/PendingSignups";
import AgentInbox from "@/pages/AgentInbox";
import Profile from "@/pages/Profile";
import EmailChangeConfirm from "@/pages/EmailChangeConfirm";
import ClientPos from "@/pages/ClientPos";
import KaryawanAbsen from "@/pages/KaryawanAbsen";
import AbsenKiosk from "@/pages/AbsenKiosk";  // Iter v34cf — Kiosk shared-device face-scan
import AdminKelolaKaryawan from "@/pages/AdminKelolaKaryawan";  // Iter v34ci — Admin manage karyawan + 5-pose face
import KaryawanRegister from "@/pages/KaryawanRegister";
import VerifyEmail from "@/pages/VerifyEmail";
import KaryawanRingkasan from "@/pages/KaryawanRingkasan";
import KaryawanChatHR from "@/pages/KaryawanChatHR";
import KaryawanChat from "@/pages/KaryawanChat";
import ClientChatCs from "@/pages/ClientChatCs";
import KaryawanCSInbox from "@/pages/KaryawanCSInbox";
import AbsensiAdmin from "@/pages/AbsensiAdmin";import AttendanceRecovery from "@/pages/AttendanceRecovery";
import AbsensiAnalytics from "@/pages/AbsensiAnalytics";
import RekapAbsensi from "@/pages/RekapAbsensi";
import FaceAuditLogs from "@/pages/FaceAuditLogs";
import OfficeLocations from "@/pages/OfficeLocations";
import HutangKaryawan from "@/pages/HutangKaryawan";
import KaryawanHutang from "@/pages/KaryawanHutang";
import KaryawanKasbon from "@/pages/KaryawanKasbon";
import KaryawanCuti from "@/pages/KaryawanCuti";
import CutiAdmin from "@/pages/CutiAdmin";
import CutiQuotas from "@/pages/CutiQuotas";
import LiburNasional from "@/pages/LiburNasional";
import KaryawanLengkapiProfil from "@/pages/KaryawanLengkapiProfil";
import KaryawanEditProfil from "@/pages/KaryawanEditProfil";
import ProfileChangeRequests from "@/pages/ProfileChangeRequests";
// Iter v34ay
import OvertimeReport from "@/pages/OvertimeReport";
import MasterTarifOvertime from "@/pages/MasterTarifOvertime";
import VendorReconciliation from "@/pages/VendorReconciliation";  // Iter v34b1
// Iter v34b2
import Payroll, { PayrollDetail } from "@/pages/Payroll";
import PayrollComponents from "@/pages/PayrollComponents";
// Iter v34b4 (fix #9)
import TambalAbsen from "@/pages/TambalAbsen";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/verify" element={<SignupVerify />} />
      <Route path="/register/karyawan" element={<KaryawanRegister />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile/confirm-email" element={<EmailChangeConfirm />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/prices" element={<PriceList />} />
        <Route path="/prices/history" element={<PriceHistory />} />
        <Route path="/pos" element={<PurchaseOrders />} />
        <Route path="/surat-jalan" element={<SuratJalan />} />
        <Route path="/stock" element={<Stock />} />
        <Route path="/stock-log" element={<StockLog />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing-list" element={<BillingList />} />
        <Route path="/pnl" element={<ProfitLoss />} />
        <Route path="/plafon-klien" element={<CreditLimits />} />{/* Iter v34cq (#7) */}
        <Route path="/users" element={<Users />} />
        <Route path="/logs" element={<SystemLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/chat" element={<Pengumuman />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/vendor-pos" element={<VendorPO />} />{/* Iter v34z (#2) */}
        <Route path="/vendors" element={<MasterVendor />} />{/* Iter v34z (#5) */}
        {/* Iter v34z (#1) — Batching Plant routes */}
        <Route path="/batching/dashboard" element={<BatchingDashboard />} />
        <Route path="/batching/po" element={<BatchingPo />} />
        <Route path="/batching/sj" element={<BatchingSj />} />
        <Route path="/batching/material-sj" element={<BatchingMaterialSj />} />
        <Route path="/batching/rekap-material" element={<BatchingMaterialRekap />} />
        <Route path="/employee-requests" element={<EmployeeRequests />} />
        <Route path="/batching/stock" element={<BatchingStock />} />
        <Route path="/batching/solar" element={<BatchingSolar />} />
        <Route path="/batching/input-material" element={<BatchingInputMaterial />} />
        <Route path="/batching/rekap" element={<BatchingRekap />} />
        <Route path="/batching/masters" element={<MasterBatching />} />{/* Iter v34z #3 Phase C */}
        <Route path="/client" element={<ClientDashboard />} />
        <Route path="/client/catalog" element={<ClientCatalog />} />
        <Route path="/client/cart" element={<ClientCart />} />
        <Route path="/client/notifications" element={<ClientNotifications />} />
        <Route path="/client/surat-jalan" element={<ClientSuratJalan />} />
        <Route path="/client/pos" element={<ClientPos />} />
        <Route path="/client/invoices" element={<ClientInvoices />} />{/* Iter v34{af} */}
        <Route path="/karyawan/absen" element={<KaryawanAbsen />} />
        <Route path="/absensi/kiosk" element={<AbsenKiosk />} />{/* Iter v34cf — Kiosk shared-device face-scan */}
        <Route path="/karyawan/kelola-admin" element={<AdminKelolaKaryawan />} />{/* Iter v34ci */}
        <Route path="/karyawan/profil" element={<KaryawanLengkapiProfil />} />
        <Route path="/karyawan/edit-profil" element={<KaryawanEditProfil />} />
        <Route path="/admin/profile-changes" element={<ProfileChangeRequests />} />
        <Route path="/karyawan/ringkasan" element={<KaryawanRingkasan />} />
        <Route path="/karyawan/chat-hr" element={<KaryawanChatHR />} />
        <Route path="/karyawan/chat-teman" element={<KaryawanChat />} />
        <Route path="/chat-karyawan" element={<KaryawanChat />} />
        <Route path="/client/chat-cs" element={<ClientChatCs />} />
        <Route path="/karyawan/inbox-hr" element={<KaryawanCSInbox />} />
        <Route path="/absensi" element={<AbsensiAdmin />} />
        <Route path="/absensi-recovery" element={<AttendanceRecovery />} />
        <Route path="/absensi/analitik" element={<AbsensiAnalytics />} />
        <Route path="/absensi/rekap" element={<RekapAbsensi />} />
        <Route path="/absensi/audit-wajah" element={<FaceAuditLogs />} />
        <Route path="/lokasi-kantor" element={<OfficeLocations />} />
        <Route path="/hutang-karyawan" element={<HutangKaryawan />} />
        <Route path="/karyawan/hutang" element={<KaryawanHutang />} />
        <Route path="/karyawan/kasbon" element={<KaryawanKasbon />} />
        <Route path="/karyawan/cuti" element={<KaryawanCuti />} />
        <Route path="/cuti-admin" element={<CutiAdmin />} />
        <Route path="/cuti-quotas" element={<CutiQuotas />} />
        <Route path="/libur-nasional" element={<LiburNasional />} />
        <Route path="/admin/pending-signups" element={<PendingSignups />} />
        <Route path="/admin/agent-inbox" element={<AgentInbox />} />
        <Route path="/laporan-lembur" element={<OvertimeReport />} />
        <Route path="/master-tarif-overtime" element={<MasterTarifOvertime />} />
        <Route path="/rekonsiliasi-vendor" element={<VendorReconciliation />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/payroll/components" element={<PayrollComponents />} />
        <Route path="/payroll/:batchId" element={<PayrollDetail />} />
        <Route path="/tambal-absen" element={<TambalAbsen />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <CartProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
